import { useDefaultEditor } from "@/hooks/use-default-editor";
import { useListEnvironments } from "@/queries/environment-queries";
import { useListPreferences, useSetPreference } from "@/queries/user-preferences-queries";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useCallback } from "react";

const DEFAULT_EDITOR_PER_ENV = "DEFAULT_EDITOR_PER_ENV";

export function useGetEditorForEnv(environmentId: string | undefined) {
    const { editorId: defaultEditorId, isPending: isPendingDefaultEditorId } = useDefaultEditor();
    const { preferences, isPending: isPendingPreferences } = useListPreferences();

    let editorIdForEnv: string | undefined;
    const editorPerEnvRaw = preferences?.find((p) => p.key === DEFAULT_EDITOR_PER_ENV)?.value;
    try {
        if (editorPerEnvRaw && environmentId) {
            const editorPerEnv = JSON.parse(editorPerEnvRaw) as [string, string][];
            editorIdForEnv = editorPerEnv.find(([k, _]) => k === environmentId)?.[1];
        }
    } catch {
        console.error("failed to parse DEFAULT_EDITOR_PER_ENV user prefence")
    }

    return {
        editorId: editorIdForEnv || defaultEditorId,
        isPending: isPendingPreferences || isPendingDefaultEditorId,
    };
}

export function useSetEditorForEnv(environmentId: string | undefined) {
    const { editorId: defaultEditorId } = useDefaultEditor();
    const setPreference = useSetPreference();
    const { data: environmentsData } = useListEnvironments()
    const { preferences } = useListPreferences();
    const editorPerEnvRaw = preferences?.find((p) => p.key === DEFAULT_EDITOR_PER_ENV)?.value;

    return useCallback(async (editorId: string) => {
        if (!environmentId || !environmentsData?.environments) {
            return;
        }
        try {
            let editorPerEnv: [string, string][] = [];
            if (editorPerEnvRaw) {
                editorPerEnv = JSON.parse(editorPerEnvRaw) as [string, string][];
            }
            if (!Array.isArray(editorPerEnv)) {
                editorPerEnv = []
            }
            const environments = environmentsData.environments.filter(
                (env) =>
                    env.spec?.desiredPhase !== EnvironmentPhase.DELETED &&
                    env.status?.phase !== EnvironmentPhase.DELETING,
            );
            const filteredEditorPerEnv = editorPerEnv.filter(([k, _]) => k !== environmentId && !!environments.find(e => e.id === k));
            if (editorId !== defaultEditorId) {
                filteredEditorPerEnv.unshift([environmentId, editorId]);
            }
            let value = JSON.stringify(filteredEditorPerEnv.slice(0, 30));
            if (value.length >= 2480) {
                // Guard against max length for user preference value defined in proto
                value = JSON.stringify(filteredEditorPerEnv.slice(0, 20));
            }
            await setPreference.mutateAsync({ key: DEFAULT_EDITOR_PER_ENV, value: JSON.stringify(filteredEditorPerEnv) });
        } catch (e) {
            console.error("Failed to update editor preference for env", e);
        }
    }, [setPreference, environmentId, defaultEditorId, editorPerEnvRaw, environmentsData]);
}
